import React from 'react';
import { useParams } from 'react-router-dom';
import { Contract } from '../types';
import {
  CalendarIcon,
  BuildingOfficeIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { formatDate, formatDueSummary, formatDueTime } from '../utils/contractFormatting';
import { getProcuringTypeMeta } from '../utils/procuringTypes';
import { DEFAULT_REGION } from '../regions';
import TruncatedTitleWithTooltip from './TruncatedTitleWithTooltip';
import OpportunityTypeBadge from './OpportunityTypeBadge';
import SaveOpportunityButton from './SaveOpportunityButton';
import { trackEvent } from '../utils/analytics';
import MatchReasons from './MatchReasons';
import { getOpportunitySourceAriaLabel, getOpportunitySourceName } from '../utils/opportunitySources';

interface ContractCardProps {
  contract: Contract;
  browseSearch?: string;
  saveSurface?: 'card' | 'saved_page';
  showMatchReasons?: boolean;
}

const ContractCard: React.FC<ContractCardProps> = ({
  contract,
  browseSearch = '',
  saveSurface = 'card',
  showMatchReasons = false,
}) => {
  const { region } = useParams();
  const targetRegion = region ?? DEFAULT_REGION;

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md">
      <header className="flex items-start gap-4">
        <div className="flex-1">
          {contract.id ? (
            <TruncatedTitleWithTooltip
              title={contract.title}
              to={`/${targetRegion}/opportunities/${contract.id}${browseSearch}`}
              className="text-lg font-semibold text-slate-900 line-clamp-2 hover:text-blue-600"
            />
          ) : (
            <TruncatedTitleWithTooltip
              title={contract.title}
              className="text-lg font-semibold text-slate-900 line-clamp-2"
            />
          )}
        </div>
        <SaveOpportunityButton opportunity={contract} surface={saveSurface} compact />
      </header>
      {showMatchReasons && <MatchReasons reasons={contract.match_reasons} className="mt-3" />}

      <div className="mt-4 flex flex-1 flex-col">
        <div className="grid grid-cols-2 gap-4 pb-2 text-sm text-slate-600">
          <Description
            icon={<BuildingOfficeIcon className="h-4 w-4" />}
            label="Industry"
            value={
              <div className="flex flex-col items-start gap-2">
                <span>{contract.industry}</span>
                {contract.opportunity_type && <OpportunityTypeBadge value={contract.opportunity_type} />}
              </div>
            }
          />
          <DueDescription contract={contract} />
        </div>

        {/* Intentionally leaving out submission instructions per updated design */}
      </div>

      <footer className="mt-auto flex items-center justify-between border-t border-slate-200 pt-4 text-xs text-slate-500">
        <ProcuringOrgBadge
          label={contract.procuring_organization || contract.city}
          procuringType={contract.procuring_type}
        />
        <div className="flex items-center gap-2">
          <a
            href={contract.source_url}
            onClick={() => trackEvent('saved_opportunity_source_clicked', { contract_id: contract.id, surface: saveSurface })}
            target="_blank"
            rel="nofollow noopener noreferrer"
            aria-label={getOpportunitySourceAriaLabel(contract.source_type)}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
          >
            {getOpportunitySourceName(contract.source_type)}
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        </div>
      </footer>
    </article>
  );
};

export default ContractCard;

const Description: React.FC<{
  icon?: React.ReactNode;
  label?: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <div className="flex min-w-0 items-start gap-2">
    {icon && <span className="mt-1 shrink-0 text-slate-400">{icon}</span>}
    <div className="min-w-0">
      {label && <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>}
      <div className="mt-0.5 break-words text-sm text-slate-700">{value}</div>
    </div>
  </div>
);

const ProcuringOrgBadge: React.FC<{ label?: string | null; procuringType?: string | null }> = ({
  label,
  procuringType,
}) => {
  if (!label) {
    return null;
  }
  const meta = getProcuringTypeMeta(procuringType);

  return (
    <span
      className="inline-flex w-fit max-w-[220px] flex-col gap-1 rounded-lg bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700"
      title={meta?.tooltip || meta?.label || undefined}
    >
      <span className="block text-sm text-slate-700 truncate">{label}</span>
      {meta?.label && (
        <span className="inline-flex items-center gap-2 text-[11px] font-medium leading-snug text-slate-500">
          <img src={meta.icon} alt={meta.label} className="h-3.5 w-3.5 shrink-0 object-contain" />
          <span className="truncate">{meta.label}</span>
        </span>
      )}
    </span>
  );
};

const DueDescription: React.FC<{ contract: Contract }> = ({ contract }) => {
  const summary = formatDueSummary(contract);
  const dueTime = formatDueTime(contract.due_at);
  return (
    <Description
      icon={<CalendarIcon className="h-4 w-4" />}
      label="Due"
      value={
        <div className="flex flex-col">
          <span>{formatDate(contract.due_date)}</span>
          {dueTime && <span className="text-xs font-medium text-slate-500">{dueTime}</span>}
          {summary && <span className="text-xs text-slate-500">{summary}</span>}
        </div>
      }
    />
  );
};
